import { useEffect, useRef, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  IconButton,
  Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../styles/viewerStyles';

/**
 * TipsWindow - A modal component for displaying contextual tips with Markdown support
 * Styled to match the viewer's dark transparent UI
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} props.title - The title to display
 * @param {string} props.markdownBody - The Markdown content to render
 * @param {boolean} props.defaultDoNotShowAgain - Initial state of the "do not show again" checkbox
 * @param {function} props.onClose - Callback when closing: (opts: { doNotShowAgain: boolean }) => void
 * @param {boolean} props.disableOverlayClose - If true, clicking overlay won't close the modal
 */
export default function TipsWindow({
  isOpen,
  title,
  markdownBody,
  defaultDoNotShowAgain = false,
  onClose,
  disableOverlayClose = false,
}) {
  const { t } = useTranslation();
  const [doNotShowAgain, setDoNotShowAgain] = useState(defaultDoNotShowAgain);
  const closeButtonRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Store the previously focused element when opening
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      // Focus the close button after the dialog opens
      setTimeout(() => {
        if (closeButtonRef.current) {
          closeButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Handle close and return focus
  const handleClose = useCallback(() => {
    onClose({ doNotShowAgain });
    // Return focus to the previous element
    if (previousActiveElement.current && previousActiveElement.current.focus) {
      setTimeout(() => {
        previousActiveElement.current.focus();
      }, 0);
    }
  }, [onClose, doNotShowAgain]);

  // Handle ESC key
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (_event, reason) => {
      if (reason === 'backdropClick' && disableOverlayClose) {
        return;
      }
      handleClose();
    },
    [handleClose, disableOverlayClose]
  );

  // Reset checkbox state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setDoNotShowAgain(defaultDoNotShowAgain);
    }
  }, [isOpen, defaultDoNotShowAgain]);

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleOverlayClick}
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 250 }}
      onKeyDown={handleKeyDown}
      maxWidth="sm"
      fullWidth
      aria-labelledby="tips-dialog-title"
      aria-describedby="tips-dialog-description"
      aria-modal="true"
      role="dialog"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        paper: {
          sx: {
            bgcolor: COLORS.darkBg,
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            maxHeight: '80vh',
            border: `1px solid ${COLORS.borderLight}`,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          },
        },
      }}
    >
      <Box sx={{ p: 2.5 }}>
        <DialogTitle
          id="tips-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 0,
            pb: 1.5,
            borderBottom: `1px solid ${COLORS.borderLight}`,
          }}
        >
          <LightbulbIcon sx={{ color: '#ffffff', fontSize: '1.3rem' }} />
          <Typography
            variant="h6"
            component="span"
            sx={{
              fontWeight: 700,
              color: COLORS.white,
              fontSize: '1rem',
            }}
          >
            {title}
          </Typography>
        </DialogTitle>

        <IconButton
          aria-label={t('Close') || 'Close'}
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: COLORS.white,
            '&:hover': {
              bgcolor: COLORS.hoverLight,
            },
          }}
        >
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>

        <DialogContent
          id="tips-dialog-description"
          sx={{
            p: 0,
            pt: 2,
            pb: 2,
            color: COLORS.white,
            fontSize: '0.875rem',
            lineHeight: 1.6,
            maxHeight: '70vh',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: COLORS.borderLight,
              borderRadius: '3px',
            },
            '& h1, & h2, & h3': {
              mt: 1.5,
              mb: 0.75,
              fontWeight: 600,
              color: COLORS.white,
            },
            '& h1': { fontSize: '1.2rem' },
            '& h2': { fontSize: '1.05rem' },
            '& h3': { fontSize: '0.95rem' },
            '& p': {
              my: 0.75,
              lineHeight: 1.6,
              color: 'rgba(255, 255, 255, 0.9)',
            },
            '& ul, & ol': {
              pl: 2.5,
              my: 0.75,
            },
            '& li': {
              mb: 0.3,
              color: 'rgba(255, 255, 255, 0.9)',
            },
            '& strong': {
              color: COLORS.white,
              fontWeight: 600,
            },
            '& code': {
              backgroundColor: COLORS.tipBg,
              color: '#81d4fa',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontFamily: 'monospace',
              fontSize: '0.85em',
            },
            '& pre': {
              backgroundColor: COLORS.tipBg,
              p: 1.5,
              borderRadius: 1,
              overflow: 'auto',
              '& code': {
                backgroundColor: 'transparent',
                p: 0,
              },
            },
            '& blockquote': {
              borderLeft: '3px solid',
              borderColor: '#ffb74d',
              pl: 1.5,
              ml: 0,
              fontStyle: 'italic',
              color: 'rgba(255, 255, 255, 0.7)',
            },
            '& a': {
              color: '#81d4fa',
              textDecoration: 'underline',
            },
            '& hr': {
              borderColor: COLORS.borderLight,
              my: 1.5,
            },
          }}
        >
          <ReactMarkdown>{markdownBody}</ReactMarkdown>
        </DialogContent>

        <DialogActions
          sx={{
            p: 0,
            pt: 1.5,
            justifyContent: 'space-between',
            borderTop: `1px solid ${COLORS.borderLight}`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={doNotShowAgain}
                onChange={(e) => setDoNotShowAgain(e.target.checked)}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  '&.Mui-checked': {
                    color: '#81d4fa',
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.8rem',
                }}
              >
                {t('tips.doNotShowAgain') || 'Do not show again'}
              </Typography>
            }
          />
          <Button
            ref={closeButtonRef}
            variant="contained"
            onClick={handleClose}
            sx={{
              minWidth: 90,
              bgcolor: 'rgba(255, 255, 255, 0.25)',
              color: COLORS.white,
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            {t('Close') || 'Close'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
